"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6 items-center">
        {[1, 2, 3].map((_, i) => <div key={i} className="h-4 w-16 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  const filteredItems = menuItems.filter(item => !/Shop|Cart|Product\s?Detail/i.test(item.label));
  return <NavigationMenu viewport={false} className="nav-menu">
      <NavigationMenuList className="gap-8 flex-row items-center">
        {filteredItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const parts = item.label.split("/");
        const cnText = parts[0]?.trim() || item.label;
        const enText = parts[1]?.trim() || "";
        return <NavigationMenuItem key={item.id} className="relative group">
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group flex flex-col items-center py-4 transition-colors">
                  <span className="text-sm font-serif font-medium tracking-[0.2em] text-primary/90 group-hover:text-primary">
                    {cnText}
                  </span>
                  {enText && <span className="text-[9px] font-sans tracking-[0.1em] opacity-50 group-hover:opacity-100 transition-opacity uppercase mt-0.5">
                      {enText}
                    </span>}
                </Link>
              </NavigationMenuLink>

              {hasChildren && <ul className={cn("absolute top-[80%] left-1/2 -translate-x-1/2 hidden group-hover:block z-50", "min-w-[160px] bg-background border border-border shadow-2xl py-1.5", "animate-in fade-in slide-in-from-top-1 duration-200", "before:content-[''] before:absolute before:-top-4 before:left-0 before:w-full before:h-4")}>
                  {item.children.map((child: any) => {
              const childParts = child.label.split("/");
              const childCn = childParts[0]?.trim() || child.label;
              const childEn = childParts[1]?.trim() || "";
              return <li key={child.id} className="group/child">
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="flex flex-col items-center justify-center px-4 py-2 hover:bg-primary/5 transition-all">
                            <span className="text-[13px] font-serif tracking-widest text-foreground/70 group-hover/child:text-primary transition-colors text-center">
                              {childCn}
                            </span>
                            {childEn && <span className="text-[9px] font-sans tracking-[0.1em] opacity-30 group-hover/child:opacity-100 mt-0.5 uppercase text-center">
                                {childEn}
                              </span>}
                          </Link>
                        </NavigationMenuLink>
                        <div className="mx-4 h-px bg-border/40 last:hidden" />
                      </li>;
            })}
                </ul>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  const toggleMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const filteredItems = menuItems.filter(item => !/Shop|Cart|Product\s?Detail/i.test(item.label));
  return <nav className="flex flex-col gap-2 p-6">
      {filteredItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = openMenus[item.id];
      const parts = item.label.split("/");
      const cnText = parts[0]?.trim() || item.label;
      const enText = parts[1]?.trim() || "";
      return <div key={item.id} data-index={i} className="flex flex-col border-b border-border/40 last:border-none pb-2">
            <div className="flex items-center justify-between py-3">
              <Link to={item.href || "#"} onClick={onItemClick} className="flex flex-col flex-1 group">
                <span className="text-xl font-serif tracking-widest text-foreground group-hover:text-primary transition-colors">
                  {cnText}
                </span>
                {enText && <span className="text-[10px] tracking-[0.2em] text-muted-foreground uppercase mt-0.5">
                    {enText}
                  </span>}
              </Link>

              {hasChildren && <button onClick={e => toggleMenu(e, item.id)} className="p-3 ml-4 flex items-center justify-center rounded-full bg-primary/5 hover:bg-primary/10 transition-colors">
                  <span className={cn("text-2xl font-light leading-none transition-transform duration-300 text-primary", isOpen ? "rotate-45" : "rotate-0")}>
                    {isOpen ? "×" : "+"}
                  </span>
                </button>}
            </div>
            
            {hasChildren && <div className={cn("overflow-hidden transition-all duration-300 ease-in-out flex flex-col gap-4 pl-4 border-l-2 border-primary/20 ml-2", isOpen ? "max-h-[1000px] opacity-100 mb-6 mt-2" : "max-h-0 opacity-0 pointer-events-none")}>
                {item.children.map((child: any) => {
            const childParts = child.label.split("/");
            const childCn = childParts[0]?.trim() || child.label;
            const childEn = childParts[1]?.trim() || "";
            return <Link key={child.id} to={child.href || "#"} onClick={onItemClick} className="flex flex-col py-1 group">
                      <span className="text-base font-serif tracking-widest text-muted-foreground group-hover:text-primary transition-colors">
                        {childCn}
                      </span>
                      {childEn && <span className="text-[9px] tracking-[0.1em] text-muted-foreground/60 uppercase">
                          {childEn}
                        </span>}
                    </Link>;
          })}
              </div>}
          </div>;
    })}
    </nav>;
}
export default function WebsiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <>
      <style dangerouslySetInnerHTML={{
      __html: `
        @media (min-width: 1025px) {
            .nav-menu {
                justify-content: flex-end;
                text-align: right;
                width: 100%;
            }
        }
      `
    }} />
      <header id="導覽列-header" className={cn("sticky top-0 z-40 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent")}>
        <div className="container max-w-none mx-auto px-4 sm:px-6 lg:px-15">
          {/* 桌面版導覽列：Logo 靠左，選單靠右 */}
          <div className="hidden md:flex h-24 items-center justify-between">
            {/* 左側：Logo */}
            <Link to="/" className="inline-block shrink-0 -ml-4 lg:ml-20">
              <img src="https://evident-moccasin.10web.cloud/wp-content/uploads/2026/04/hents-flooring-logo.png" alt="Hent's 亨特地板" className="h-24 w-auto object-contain" />
            </Link>

            {/* 右側：導覽選單 */}
            <div className="flex items-center">
              <MenuProvider menu_id="58">
                <HeaderMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          {/* 手機版導覽列：保持不變 */}
          <div className="flex md:hidden h-20 items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="開啟選單" className="-ml-2 text-primary">
              <Menu className="size-6" />
            </Button>

            <Link to="/" className="absolute left-1/2 -translate-x-1/2">
              <img src="https://evident-moccasin.10web.cloud/wp-content/uploads/2026/04/hents-flooring-logo.png" alt="Hent's 亨特地板" className="h-20 w-auto object-contain" />
            </Link>

            <div className="flex items-center gap-2 -mr-2" />
          </div>
        </div>
      </header>

      {/* 手機版側邊選單：保持不變 */}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background flex flex-col animate-in fade-in duration-200">
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://evident-moccasin.10web.cloud/wp-content/uploads/2026/04/hents-flooring-logo.png" alt="Hent's 亨特地板" className="h-20 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="關閉選單" className="-mr-2 text-primary">
              <X className="size-6" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            <MenuProvider menu_id="58">
              <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

          
          </div>
        </div>}
    </>;
}